import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles,
    HeartHandshake,
    Globe2,
    ArrowLeft,
    Search,
    Eye,
    Save,
    Loader2,
    Settings2,
    FolderOpen,
    KeyRound,
} from "lucide-react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import TierBadge from "../../components/TierBadge";
import PremiumSpaceTab from "../../components/partner/admin/PremiumSpaceTab";
import ImpactStoriesTab from "../../components/partner/admin/ImpactStoriesTab";
import GratitudeMessagesTab from "../../components/partner/admin/GratitudeMessagesTab";
import AssoResourcesTab from "../../components/partner/admin/AssoResourcesTab";
import PartnerAccountTab from "./PartnerAccountTab";

/**
 * Iter-27.63 — Backoffice "Espace Partenaires" séparé de /admin?tab=associations.
 *
 * Mission : éditer la couche premium d'une asso (branding, tier, hero, features,
 * remerciements, histoires d'impact) sans toucher au contenu pédagogique de la
 * page formation.
 *
 * Routes :
 *   - /admin/espace-partenaires        → liste
 *   - /admin/espace-partenaires/:id    → éditeur de l'asso choisie
 */
export default function AdminPartnerSpaces() {
    const [items, setItems] = useState(null);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState("");

    const reload = async () => {
        try {
            const r = await api.get("/admin/associations");
            setItems(r.data || []);
        } catch {
            setItems([]);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    const filtered = useMemo(() => {
        if (!items) return [];
        const q = search.trim().toLowerCase();
        return items.filter((a) => !q || (a.name || "").toLowerCase().includes(q));
    }, [items, search]);

    if (editing) {
        return (
            <PartnerSpaceEditor
                asso={editing}
                onClose={() => setEditing(null)}
                onChanged={reload}
            />
        );
    }

    return (
        <Layout title="Espace Partenaires" subtitle="Gestion de la couche premium des associations">
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                <Link
                    to="/admin"
                    className="inline-flex items-center gap-2 text-white/60 hover:text-shariti-orange text-sm font-bold transition-colors"
                    data-testid="back-to-admin"
                >
                    <ArrowLeft className="w-4 h-4" /> Retour Backoffice
                </Link>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher une asso…"
                        data-testid="partner-search"
                        className="bg-white/5 border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white outline-none focus:border-shariti-orange/40 w-64 max-w-full"
                    />
                </div>
            </div>

            {items === null ? (
                <div className="glass-card rounded-3xl p-10 text-center text-white/50">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="glass-card rounded-3xl p-10 text-center text-white/55">
                    <Sparkles className="w-10 h-10 text-shariti-yellow/50 mx-auto mb-3" />
                    <p>Aucune association à configurer pour l&apos;instant.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((a) => (
                        <PartnerCardAdmin
                            key={a.association_id}
                            a={a}
                            onEdit={() => setEditing(a)}
                        />
                    ))}
                </div>
            )}
        </Layout>
    );
}

function PartnerCardAdmin({ a, onEdit }) {
    const tier = a.tier || "engaged";
    const gratitude = (a.features || {}).gratitude !== false;
    const impactStories = (a.features || {}).impact_stories !== false;
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl overflow-hidden bg-white/3 border border-white/10 hover:border-shariti-yellow/30 transition-all group"
            data-testid={`admin-partner-card-${a.association_id}`}
        >
            <div className="relative aspect-[16/9]">
                {a.hero_image_url || a.cover_image ? (
                    <img
                        src={a.hero_image_url || a.cover_image}
                        alt={a.name}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(135deg, ${a.primary_color || "#EE722E"} 0%, rgba(24,24,24,0.85) 100%)`,
                        }}
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-shariti-black via-shariti-black/40 to-transparent" />
                <div className="absolute top-3 left-3 right-3 flex justify-between items-start gap-2">
                    <TierBadge tier={tier} size="sm" />
                    {a.logo_url && (
                        <div className="w-9 h-9 rounded-xl bg-white p-1 flex items-center justify-center">
                            <img
                                src={a.logo_url}
                                alt=""
                                className="w-full h-full object-contain"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="p-4 space-y-3">
                <div>
                    <h3 className="text-base font-extrabold text-white leading-tight">
                        {a.name}
                    </h3>
                    {a.tagline && (
                        <p className="text-[11px] text-white/55 mt-0.5 line-clamp-1">
                            {a.tagline}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <FeatureChip on={gratitude} icon={HeartHandshake} label="Remerciements" />
                    <FeatureChip on={impactStories} icon={Globe2} label="Impact" />
                </div>
                <div className="flex items-center gap-2 pt-1">
                    <button
                        onClick={onEdit}
                        data-testid={`admin-partner-edit-${a.association_id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-shariti-yellow/15 hover:bg-shariti-yellow/25 border border-shariti-yellow/40 text-shariti-yellow font-extrabold py-2 px-3 rounded-2xl text-xs transition-all"
                    >
                        <Settings2 className="w-3.5 h-3.5" />
                        Configurer
                    </button>
                    <Link
                        to={`/espace-partenaires/${a.association_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`admin-partner-preview-${a.association_id}`}
                        className="inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white font-bold py-2 px-3 rounded-2xl text-xs transition-all"
                        title="Voir comme un ambassadeur"
                    >
                        <Eye className="w-3.5 h-3.5" />
                        Preview
                    </Link>
                </div>
            </div>
        </motion.div>
    );
}

function FeatureChip({ on, icon: Icon, label }) {
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full ${
                on
                    ? "bg-shariti-orange/15 text-shariti-orange-light"
                    : "bg-white/5 text-white/35 line-through decoration-1"
            }`}
        >
            <Icon className="w-3 h-3" />
            {label}
        </span>
    );
}

/**
 * Éditeur d'un Espace Partenaire pour une asso donnée.
 * 3 onglets : Branding & Tier / Remerciements / Histoires d'impact.
 */
function PartnerSpaceEditor({ asso, onClose, onChanged }) {
    const [tab, setTab] = useState("branding");
    const [form, setForm] = useState({
        tier: asso.tier || "engaged",
        primary_color: asso.primary_color || "",
        accent_color: asso.accent_color || "",
        hero_image_url: asso.hero_image_url || "",
        hero_video_url: asso.hero_video_url || "",
        tagline: asso.tagline || "",
        features: asso.features || {},
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/associations/${asso.association_id}`, form);
            toast.success("Espace partenaire mis à jour");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout title={asso.name} subtitle="Espace partenaire — édition">
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                <button
                    onClick={onClose}
                    className="inline-flex items-center gap-2 text-white/60 hover:text-shariti-orange text-sm font-bold transition-colors"
                    data-testid="back-to-partner-list"
                >
                    <ArrowLeft className="w-4 h-4" /> Liste des espaces
                </button>
                <Link
                    to={`/espace-partenaires/${asso.association_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="preview-partner-space"
                    className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 font-bold px-4 py-2 rounded-2xl text-sm"
                >
                    <Eye className="w-4 h-4" /> Voir comme un ambassadeur
                </Link>
            </div>

            <div className="glass-card rounded-3xl p-5 sm:p-7">
                {/* Tabs */}
                <div className="overflow-x-auto -mx-1 px-1 mb-5">
                    <div className="flex gap-1 p-1 glass-card rounded-full w-max text-xs">
                        {[
                            { id: "branding", label: "Branding & Tier", Icon: Sparkles },
                            { id: "account", label: "Compte partenaire", Icon: KeyRound },
                            { id: "gratitude", label: "Remerciements", Icon: HeartHandshake },
                            { id: "impact", label: "Histoires d'impact", Icon: Globe2 },
                            { id: "resources", label: "Ressources", Icon: FolderOpen },
                        ].map((t) => {
                            const Icon = t.Icon;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setTab(t.id)}
                                    data-testid={`partner-tab-${t.id}`}
                                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold transition-all whitespace-nowrap ${
                                        tab === t.id
                                            ? "bg-shariti-orange text-white"
                                            : "text-white/60 hover:text-white"
                                    }`}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {tab === "branding" && (
                    <>
                        <PremiumSpaceTab form={form} setForm={setForm} />
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="bg-white/5 hover:bg-white/10 text-white/80 font-bold px-4 py-2.5 rounded-2xl"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="partner-save"
                                className="inline-flex items-center gap-2 bg-shariti-yellow text-shariti-black font-extrabold px-5 py-2.5 rounded-2xl disabled:opacity-40"
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                Enregistrer le branding
                            </button>
                        </div>
                    </>
                )}

                {tab === "gratitude" && (
                    <GratitudeMessagesTab assoId={asso.association_id} />
                )}

                {/* Iter-33 — Compte partenaire : email + reset password de l'asso.
                 * Permet à l'admin de communiquer directement les identifiants
                 * (création + régénération de mot de passe visible UNE fois). */}
                {tab === "account" && (
                    <PartnerAccountTab
                        assoId={asso.association_id}
                        assoName={asso.name}
                    />
                )}

                {tab === "impact" && <ImpactStoriesTab assoId={asso.association_id} />}

                {/* Iter-33 — Onglet Ressources côté admin Shariti : permet à un
                 * admin de déposer un fichier au nom d'une asso (cas classique :
                 * l'asso nous envoie un PPT/PDF par mail après une formation et
                 * demande à Shariti de le diffuser aux ambassadeurs). */}
                {tab === "resources" && (
                    <AssoResourcesTab
                        adminMode
                        assoId={asso.association_id}
                        onChanged={onChanged}
                    />
                )}
            </div>
        </Layout>
    );
}
